import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  ListChatsPaginationDocument,
  ListPendingUserRequestsDocument,
  Project,
  useCreateUserRequestMutation,
  UserRequestable,
  UserRequestCategory,
} from '@graphql/generated';

interface InviteRequestButtonProps {
  currentUserHasPendingMemberAdditionRequest?: boolean;
  projectId?: Project['id'];
}

export const InviteRequestButton: React.FC<InviteRequestButtonProps> = ({
  currentUserHasPendingMemberAdditionRequest,
  projectId,
}) => {
  const { t } = useTranslation(['models', 'shared']);

  const [sendRequestToJoin, setSendRequestToJoin] = useState(
    currentUserHasPendingMemberAdditionRequest
  );

  useEffect(() => {
    setSendRequestToJoin(currentUserHasPendingMemberAdditionRequest);
  }, [currentUserHasPendingMemberAdditionRequest]);

  const [createUserRequest, { data: createUserRequestData }] =
    useCreateUserRequestMutation({
      refetchQueries: [
        { query: ListChatsPaginationDocument, variables: { first: 20 } },
        'listMessages',
        'getProject',
        {
          query: ListPendingUserRequestsDocument,
          variables: {
            attributes: {
              requestType: UserRequestCategory.MemberAddition,
              userRequestableId: projectId || '',
              userRequestableType: UserRequestable.Project,
            },
          },
        },
      ],
    });

  useEffect(() => {
    if (!createUserRequestData) return;
    setSendRequestToJoin(true);
  }, [createUserRequestData]);
  const sendRequest = () => {
    createUserRequest({
      variables: {
        attributes: {
          requestType: UserRequestCategory.MemberAddition,
          userRequestableId: projectId || '',
          userRequestableType: UserRequestable.Project,
        },
      },
    });
  };

  return (
    <Box
      flexDirection='column'
      justifyContent='center'
      alignItems='center'
      marginTop='s'
      paddingVertical='l'>
      <Icon
        height={35}
        width={35}
        color='grey05'
        name='AlertTriangle'
        marginBottom='xs'></Icon>

      <Box marginBottom='m' justifyContent='center' flexDirection='row'>
        <Text variant='body1' color='textSecondary'>
          {t('shared:youreNotAMemberOfThisProject')}
        </Text>
      </Box>
      <Box width={180} height={48}>
        <Button
          backgroundColor='black'
          prefix={
            <Icon
              variant='l'
              name='UserCheck'
              color={!sendRequestToJoin ? 'white' : 'grey04'}
            />
          }
          onPress={() => !sendRequestToJoin && sendRequest()}
          borderRadius='xl'
          variant={
            !sendRequestToJoin ? 'editWhite' : 'editSelectedDisabledDark'
          }
          prefixMarginRight='s'
          disabled={sendRequestToJoin}
          accessibilityLabel={t('models:projects.requestToJoin')}>
          {!sendRequestToJoin
            ? t('models:projects.requestToJoin')
            : t('models:projects.pendingRequest')}
        </Button>
      </Box>
    </Box>
  );
};
